import { defineStore } from 'pinia'
import * as api from '@/api'
import { Question, Flow, Selection, ServicePackageResponse, ServicePackageRequest, LoadingScreen } from '@/model'
import { Service, ServicePackage } from '@/types/ServicePackage'
import { makePackageName, selectionsServices } from '@/utils/services'
import { ServiceTypes } from '@/enums'
import { useGlobal } from '@/store'
import { trackEvent, trackFlow, trackUserSelection } from '@/utils/analytics'
import { addAdobeDataLayerPageView, trackSelectionEventCJA } from '@/utils/adobe-analytics-data-layer'

interface FlowState {
  flow: Flow | null;
  progress?: any;
  currentQuestion?: Question;
  busy: boolean;
  // this may contain answers from the future, or from a different flow
  selectionHistory: Record<string, { selections: Selection[], label?: string }>;
  // this will have the recorded answers from start of flow up to the last answered question
  selectedAnswers: Record<string, { selections: Selection[], label?: string }>;
  estimatedOutOfPocketPriceMap: Record<string, ServicePackageResponse>
  declineAmountMap: Record<string, ServicePackageResponse>
  initialAllowanceAmount: number,
  initialDeclineAmount: number,
  isEditMode: boolean,
  loadingScreen: LoadingScreen,
  serviceType?: ServiceTypes
}

const showErrorModal = () => {
  const global = useGlobal()
  global.$patch({ isErrorModalOpened: true })
}

const store = defineStore('flow', {
  state: (): FlowState => {
    return {
      flow: null,
      progress: undefined,
      currentQuestion: undefined,
      busy: true,
      selectionHistory: {},
      selectedAnswers: {},
      estimatedOutOfPocketPriceMap: {},
      declineAmountMap: {},
      initialDeclineAmount: 0,
      initialAllowanceAmount: 0,
      isEditMode: false,
      loadingScreen: {} as LoadingScreen,
      serviceType: undefined
    }
  },
  getters: {
    hasPreviousQuestion: (state) => {
      return !!state.flow?.hasPreviousQuestion
    },
    serviceHistory: (state): Service[] => {
      return selectionsServices(Object.values(state.selectedAnswers).map(({ selections }) => selections) as Selection[][])
    },
    serviceHistoryExcludingCurrentQuestion(state): Service[] {
      if (!state.currentQuestion) {
        return this.serviceHistory
      }

      const { [state.currentQuestion?.uid]: _, ...otherSelectedAnswers } = state.selectedAnswers
      const values = Object.values(otherSelectedAnswers).map(({ selections }) => selections) as Selection[][]
      return selectionsServices(values)
    },
    startDeclineAmount(state): (currentSelection: Selection[]) => number {
      return (currentSelection: Selection[]) => {
        const packageName = makePackageName([
          ...this.serviceHistoryExcludingCurrentQuestion,
          ...currentSelection.filter(({ service }) => !!service).map(({ service }) => service as Service)
        ])
        return state.declineAmountMap[packageName]?.startDeclineAmount || 0
      }
    },
    remainingDeclineAmount(state): (currentSelection: Selection[]) => number {
      return (currentSelection: Selection[]) => {
        const packageName = makePackageName([
          ...this.serviceHistoryExcludingCurrentQuestion,
          ...currentSelection.filter(({ service }) => !!service).map(({ service }) => service as Service)
        ])
        return state.declineAmountMap[packageName]?.remainingDeclineAmount || 0
      }
    },
    allowanceAmount(state): (currentSelection: Selection[]) => number {
      return (currentSelection: Selection[]) => {
        const packageName = makePackageName([
          ...this.serviceHistoryExcludingCurrentQuestion,
          ...currentSelection.filter(({ service }) => !!service).map(({ service }) => service as Service)
        ])
        return state.declineAmountMap[packageName]?.allowanceAmount || 0
      }
    },
    estimatedOutOfPocketCost(state): (currentSelection: Selection[]) => number {
      return (currentSelection: Selection[]) => {
        const packageName = makePackageName([
          ...this.serviceHistoryExcludingCurrentQuestion,
          ...currentSelection.filter(({ service }) => !!service).map(({ service }) => service as Service)
        ])
        return state.estimatedOutOfPocketPriceMap[packageName]?.totalMoop || 0
      }
    },
    pricingSummary(state) {
      const packageName = makePackageName(this.serviceHistory)
      return state.estimatedOutOfPocketPriceMap[packageName]
    },
    selectionSummary(): Array<{ label: string, answer: string }> {
      if (this.serviceType === ServiceTypes.exam) {
        const label = Object.values(this.selectedAnswers)[0].selections[0].toString()
        return [{ label, answer: '' }]
      }

      return Object.values(this.selectedAnswers)
        .filter(a => a.label)
        .map(a => ({
          label: a.label as string,
          answer: a.selections.map(s => s.toString()).join(', ')
        }))
    }
  },
  actions: {
    async getFlow(flowId: string, locale: string) {
      this.busy = true

      try {
        const flowPromise = api.cms.fetchFlow(flowId, locale)
        const loadingScreenPromise = api.cms.fetchFinalLoadingScreen(locale)
        const [flow, loadingScreen] = await Promise.all([flowPromise, loadingScreenPromise])

        this.$patch({
          busy: false,
          flow,
          loadingScreen
        })
      } catch (e) {
        showErrorModal()
      }
    },
    async startFlow(): Promise<void> {
      if (!this.flow) {
        return
      }

      this.flow.resetEditSelectionsQuestions()
      const firstQuestion = this.flow.getFirstQuestion()
      this.estimatedOutOfPocketPriceMap = {}
      this.declineAmountMap = {}
      this.initialAllowanceAmount = 0
      this.initialDeclineAmount = 0
      this.currentQuestion = firstQuestion
      this.selectedAnswers = {}
      this.selectionHistory = {}
      this.isEditMode = false
      this.progress = this.flow.progress
      await this.updateEstimatedOutOfPocketPriceMap(firstQuestion.services, false)
    },
    async goToNextQuestion(answers: Selection[]): Promise<boolean> {
      if (!this.flow) {
        return false
      }

      if (!answers.length || !this.currentQuestion) {
        return false
      }

      // answers.forEach(selection => trackEvent(selection.eventName))

      answers.forEach(selection => { trackSelectionEventCJA(selection.eventName) })

      if (answers[0].answer.serviceType) {
        this.serviceType = answers[0].answer.serviceType
        trackFlow(this.serviceType)
      }

      const nextQuestion = this.flow.getNextQuestion(answers[0].answer.uid)

      this.selectedAnswers = {
        ...this.selectedAnswers,
        [this.currentQuestion.uid]: { selections: answers, label: this.currentQuestion.label }
      }

      this.selectionHistory = {
        ...this.selectionHistory,
        [this.currentQuestion.uid]: { selections: answers, label: this.currentQuestion.label }
      }

      const selectedServiceType = this.serviceType

      const flowName = selectedServiceType === ServiceTypes.exam
        ? 'exam'
        : selectedServiceType === ServiceTypes.contacts
          ? 'contacts'
          : selectedServiceType === ServiceTypes.glasses
            ? 'glasses'
            : undefined

      addAdobeDataLayerPageView({
        pageName: this.currentQuestion.title || 'flow',
        flowName,
        options: Object.values(this.selectedAnswers)
          .map(({ selections }) => selections
            .map((selection: { name: any }) => selection.name)
            .join(', ')
          )
      })

      if (!nextQuestion) {
        return false
      }

      await this.updateEstimatedOutOfPocketPriceMap(nextQuestion.services, true)

      this.currentQuestion = nextQuestion
      this.progress = this.flow.progress
      return true
    },
    async updateEstimatedOutOfPocketPriceMap(services: Service[], includeHistory = false, treatAsAnswer = false) {
      const history = includeHistory
        ? treatAsAnswer
          ? this.serviceHistoryExcludingCurrentQuestion
          : this.serviceHistory
        : []
      let packages: ServicePackage[]
      if (!services.length) {
        return
      }
      if (treatAsAnswer) {
        packages = [{
          services: [...history, ...services],
          packageName: makePackageName([...history, ...services])
        }]
      } else {
        packages = services.map(service => ({
          services: [...history, service],
          packageName: makePackageName([...history, service])
        }))
      }
      packages = packages.filter((servicePackage: ServicePackage): boolean => {
        return this.estimatedOutOfPocketPriceMap[servicePackage.packageName] === undefined
      })
      if (!packages.length) {
        return
      }
      try {
        const serviceResponse = await api.price.fetchPrice(ServicePackageRequest.of(packages))
        this.initialAllowanceAmount = serviceResponse[0]?.allowanceAmount || 0
        this.initialDeclineAmount = serviceResponse[0]?.startDeclineAmount || 0
        const prices = (serviceResponse).reduce((acc: Record<string, ServicePackageResponse>, current) => ({ ...acc, [current.packageName]: current }), {})
        this.estimatedOutOfPocketPriceMap = { ...this.estimatedOutOfPocketPriceMap, ...prices }
        this.declineAmountMap = { ...this.declineAmountMap, ...prices }
      } catch (e) {
        showErrorModal()
        throw e
      }
    },

    async updatePriceComparisonWithEye360() {
      const servicePackage = {
        services: [...this.serviceHistory],
        packageName: makePackageName([...this.serviceHistory])
      }

      try {
        const prices = (await api.price.fetchPrice(ServicePackageRequest.of([servicePackage], true)))
          .reduce((acc: Record<string, ServicePackageResponse>, current) => ({ ...acc, [current.packageName]: current }), {})
        this.estimatedOutOfPocketPriceMap = { ...this.estimatedOutOfPocketPriceMap, ...prices }
      } catch (e) {
        showErrorModal()
        throw e
      }
    },
    goToPreviousQuestion() {
      if (!this.flow) {
        return
      }
      if (this.currentQuestion && this.selectedAnswers[this.currentQuestion.uid]) {
        const { [this.currentQuestion.uid]: _, ...otherSelectedAnswers } = this.selectedAnswers
        this.selectedAnswers = {
          ...otherSelectedAnswers
        }
      }

      if (!this.isEditMode && this.currentQuestion) {
        const { [this.currentQuestion.uid]: _, ...otherAnswers } = this.selectionHistory
        this.selectionHistory = {
          ...otherAnswers
        }
      }

      this.currentQuestion = this.flow.getPreviousQuestion()
      this.progress = this.flow.progress
    },
    goToEditSelectionQuestion() {
      if (!this.flow) {
        return
      }

      this.currentQuestion = this.flow.getEditSelectionsQuestion()
      this.progress = this.flow.progress
      this.selectedAnswers = {}
      this.isEditMode = true
    }
  }
})

export default store
